import type {
  ContributedInstructionMetadata,
  ContributedInstructionsMetadata,
} from '@github-ui/ahp-session-client/contracts'
import {ChecklistIcon, MarkGithubIcon, NoteIcon, RepoIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Link, Portal, Tooltip as PrimerTooltip, VisuallyHidden} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {type ReactElement, type RefObject, useCallback, useEffect, useRef, useState} from 'react'

import type {ContributedInstruction, ContributedInstructionSnapshot} from '../utils/instruction-rules'

type ResolvedInstruction = {
  kind: ContributedInstructionMetadata['kind']
  label: string
  prompt?: string
  ownerUrl?: string
}

export interface AppliedInstructionsDialogProps {
  contributedInstruction: ContributedInstructionMetadata
  currentInstructions: ContributedInstructionSnapshot
  onDismiss: () => void
  returnFocusRef: RefObject<HTMLElement | null>
}

export interface AppliedInstructionsMenuProps {
  contributedInstructions: ContributedInstructionsMetadata
  onSelect: (instruction: ContributedInstructionMetadata, trigger: HTMLElement) => void
}

function instructionKindLabel(kind: ContributedInstructionMetadata['kind']): string {
  switch (kind) {
    case 'personal':
      return 'Personal instructions'
    case 'repository':
      return 'Repository instructions'
    case 'organization':
      return 'Organization instructions'
  }
}

function instructionKindIcon(kind: ContributedInstructionMetadata['kind']): ReactElement {
  switch (kind) {
    case 'personal':
      return <NoteIcon aria-hidden="true" />
    case 'repository':
      return <RepoIcon aria-hidden="true" />
    case 'organization':
      return <MarkGithubIcon aria-hidden="true" />
  }
}

export function AppliedInstructionsMenu({
  contributedInstructions,
  onSelect,
}: AppliedInstructionsMenuProps): ReactElement {
  const [open, setOpen] = useState(false)
  const [pendingSelection, setPendingSelection] = useState<{
    instruction: ContributedInstructionMetadata
    trigger: HTMLElement
  }>()
  const triggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!pendingSelection || open) return

    const frame = requestAnimationFrame(() => {
      onSelect(pendingSelection.instruction, pendingSelection.trigger)
      setPendingSelection(undefined)
    })

    return () => cancelAnimationFrame(frame)
  }, [onSelect, open, pendingSelection])

  const handleSelect = useCallback((instruction: ContributedInstructionMetadata) => {
    const trigger = triggerRef.current
    if (!trigger) return

    setOpen(false)
    setPendingSelection({instruction, trigger})
  }, [])

  return (
    <ActionMenu anchorRef={triggerRef} open={open} onOpenChange={setOpen}>
      <PrimerTooltip text="Applied instructions">
        <ActionMenu.Button
          aria-label="Applied instructions"
          size="small"
          variant="invisible"
          leadingVisual={ChecklistIcon}
          trailingAction={null}
        />
      </PrimerTooltip>
      <ActionMenu.Overlay align="end">
        <ActionList>
          <ActionList.Group>
            <ActionList.GroupHeading>Applied instructions</ActionList.GroupHeading>
            {contributedInstructions.map(instruction => (
              <ActionList.Item
                key={`${instruction.kind}-${instruction.label ?? ''}-${instruction.ownerUrl ?? ''}`}
                onSelect={() => handleSelect(instruction)}
              >
                <ActionList.LeadingVisual>{instructionKindIcon(instruction.kind)}</ActionList.LeadingVisual>
                {instructionKindLabel(instruction.kind)}
                {instruction.label && instruction.label !== instructionKindLabel(instruction.kind) && (
                  <ActionList.Description variant="block">{instruction.label}</ActionList.Description>
                )}
              </ActionList.Item>
            ))}
          </ActionList.Group>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

function matchesInstruction(row: ContributedInstructionMetadata, candidate: ContributedInstruction): boolean {
  if (row.kind !== candidate.kind) return false
  if (!row.label) return row.kind === 'personal'
  return row.label.trim().toLowerCase() === candidate.label.trim().toLowerCase()
}

function safeInstructionUrl(
  url: string | undefined,
  kind: ContributedInstructionMetadata['kind'],
  label: string | undefined,
): string | undefined {
  if (!url || !label || kind === 'personal' || !URL.canParse(url)) return undefined

  try {
    const parsed = new URL(url, 'https://github.com')
    const expectedPath = `/${label}`.toLowerCase()
    const path = parsed.pathname.replace(/\/$/, '').toLowerCase()
    return parsed.protocol === 'https:' &&
      parsed.hostname === 'github.com' &&
      !parsed.port &&
      !parsed.username &&
      !parsed.password &&
      !parsed.search &&
      !parsed.hash &&
      path === expectedPath
      ? parsed.href
      : undefined
  } catch {
    return undefined
  }
}

function resolveInstruction(
  row: ContributedInstructionMetadata,
  currentInstructions: ContributedInstructionSnapshot,
): ResolvedInstruction {
  const current = currentInstructions.find(candidate => matchesInstruction(row, candidate))
  const ownerUrl =
    safeInstructionUrl(row.ownerUrl, row.kind, row.label) ??
    safeInstructionUrl(current?.ownerUrl, current?.kind ?? row.kind, current?.label)
  return {
    kind: row.kind,
    label: row.label ?? current?.label ?? '',
    ...(current?.prompt?.trim() ? {prompt: current.prompt} : {}),
    ...(ownerUrl ? {ownerUrl} : {}),
  }
}

export function AppliedInstructionsDialog({
  contributedInstruction,
  currentInstructions,
  onDismiss,
  returnFocusRef,
}: AppliedInstructionsDialogProps): ReactElement {
  const instruction = resolveInstruction(contributedInstruction, currentInstructions)
  const subtitleContent =
    instruction.label || instruction.ownerUrl ? (
      <>
        {instruction.label}
        {instruction.label && instruction.ownerUrl && ' · '}
        {instruction.ownerUrl && instruction.kind !== 'personal' && (
          <Link href={instruction.ownerUrl} target="_blank" rel="noreferrer">
            {instruction.kind === 'repository' ? 'View repository' : 'View organization'}
            <VisuallyHidden>
              {` for ${instruction.label || instructionKindLabel(instruction.kind)} (opens in a new tab)`}
            </VisuallyHidden>
          </Link>
        )}
      </>
    ) : undefined

  return (
    <Portal>
      <Dialog
        onClose={onDismiss}
        title={instructionKindLabel(instruction.kind)}
        width="large"
        returnFocusRef={returnFocusRef}
        renderHeader={({dialogLabelId, dialogDescriptionId, onClose}) => (
          <Dialog.Header>
            <div className="d-flex">
              <div className="d-flex flex-column flex-1 min-width-0 px-2 py-1">
                <Dialog.Title id={dialogLabelId}>{instructionKindLabel(instruction.kind)}</Dialog.Title>
                {subtitleContent && <Dialog.Subtitle id={dialogDescriptionId}>{subtitleContent}</Dialog.Subtitle>}
              </div>
              <Dialog.CloseButton onClose={() => onClose('close-button')} />
            </div>
          </Dialog.Header>
        )}
        renderBody={() => (
          <Dialog.Body>
            {instruction.prompt?.trim() ? (
              <>
                <p className="text-bold mb-2">Current instruction text</p>
                <p className="mb-0" style={{whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}}>
                  {instruction.prompt}
                </p>
              </>
            ) : (
              <p className="color-fg-muted mb-0">The current instruction text is unavailable for this response.</p>
            )}
          </Dialog.Body>
        )}
      />
    </Portal>
  )
}
